class VideoPlayer extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = this.querySelector('video');
  /** @type {HTMLButtonElement | null} */
  #playButton = this.querySelector('button[data-play-pause-button]');
  /** @type {HTMLButtonElement | null} */
  #soundButton = this.querySelector('button[data-sound-button]');
  /** @type {boolean} */
  #lazy = this.hasAttribute('data-lazy');
  /** @type {boolean} */
  #autoplay = this.hasAttribute('data-autoplay');
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {boolean} */
  #userInteracted = false;

  /** @type {() => void} */
  #onPlayButtonClick = this.#handlePlayButtonClick.bind(this);
  /** @type {() => void} */
  #onVideoEnded = this.#togglePlayButton.bind(this);
  /** @type {() => void} */
  #onSoundButtonClick = this.#handleSoundButtonClick.bind(this);

  connectedCallback() {
    if (!this.#video) return;

    this.#handleAccessibility();

    if (this.#lazy) {
      this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection.bind(this));
      this.#intersectionObserver.observe(this.#video);
    }

    this.#playButton?.addEventListener('click', this.#onPlayButtonClick);
    this.#video.addEventListener('ended', this.#onVideoEnded);
    this.#soundButton?.addEventListener('click', this.#onSoundButtonClick);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();

    this.#playButton?.removeEventListener('click', this.#onPlayButtonClick);
    this.#video?.removeEventListener('ended', this.#onVideoEnded);
    this.#soundButton?.removeEventListener('click', this.#onSoundButtonClick);
  }

  /**
   * Handle intersection of the video element.
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries.
   * @returns {void}
   */
  #handleIntersection(entries) {
    entries.forEach((entry) => {
      // Only play/pause video if user has interacted with it or if autoplay is enabled.
      const isToggleNeeded = this.#userInteracted || this.#autoplay;
      if (!isToggleNeeded) return;

      entry.isIntersecting ? this.#video.play() : this.#video.pause();
      this.#togglePlayButton();
    });
  }

  /**
   * Handle accessibility preferences by disabling autoplay and pausing video.
   * @returns {void}
   */
  #handleAccessibility() {
    if (!this.#autoplay) return;

    const isReducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouchscreen = matchMedia('(pointer: coarse)').matches;
    const isBatterySaver = isTouchscreen && navigator.connection?.saveData;

    if (isReducedMotion || isBatterySaver) {
      this.#disableAutoplay();
      return;
    }

    if (isTouchscreen && typeof navigator.getBattery === 'function') {
      navigator.getBattery().then((battery) => {
        if (!this.#autoplay) return;
        if (!battery.charging && battery.level < 0.2) this.#disableAutoplay();
      });
    }
  }

  /**
   * Disable autoplay and pause the video.
   * @returns {void}
   */
  #disableAutoplay() {
    this.#autoplay = false;
    this.#video.pause();
    this.#togglePlayButton();
  }

  /**
   * Handle button click to toggle video play/pause state.
   * @returns {void}
   */
  #handlePlayButtonClick() {
    this.#video.paused ? this.#video.play() : this.#video.pause();
    this.#userInteracted = true;
    this.#togglePlayButton();
  }

  /**
   * Toggle visibility of a pair of icons: one shown when condition is true, the other when false.
   * @param {HTMLElement | null} container - Element containing the two icon elements.
   * @param {string} selectorFirst - Selector for the icon to show when showFirst is true.
   * @param {string} selectorSecond - Selector for the icon to show when showFirst is false.
   * @param {boolean} showFirst - When true, first icon visible and second hidden; when false, opposite.
   * @returns {void}
   */
  #toggleIconPair(container, selectorFirst, selectorSecond, showFirst) {
    const first = container?.querySelector(selectorFirst);
    const second = container?.querySelector(selectorSecond);
    first?.classList.toggle('hidden', !showFirst);
    second?.classList.toggle('hidden', showFirst);
  }

  /** Toggle visibility of play and pause icons based on video state.
   * @returns {void}
   */
  #togglePlayButton() {
    if (!this.#playButton) return;
    this.#toggleIconPair(
      this.#playButton,
      '[data-icon="regular-play"]',
      '[data-icon="regular-pause"]',
      this.#video.paused
    );
  }

  /** Handle sound button click to toggle video muted state.
   * @returns {void}
   */
  #handleSoundButtonClick() {
    this.#video.muted = !this.#video.muted;
    this.#toggleSoundButton();
  }

  /** Toggle visibility of volume and volume-off icons based on muted state.
   * @returns {void}
   */
  #toggleSoundButton() {
    if (!this.#soundButton) return;
    this.#toggleIconPair(
      this.#soundButton,
      '[data-icon="regular-volume-on"]',
      '[data-icon="regular-volume-off"]',
      !this.#video.muted
    );
  }
}

customElements.define('video-player', VideoPlayer);
